import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaUsers, FaCheckCircle, FaClock, FaEnvelope, FaUserCheck, FaExclamationTriangle, FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import apiClient from '../../apiClient';
import './ParticipationTrackingModal.css';

const ParticipationTrackingModal = ({ isOpen, onClose, assessmentId, assessmentType }) => {
  const [participationData, setParticipationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedResults, setExpandedResults] = useState({});
  const [loadingResults, setLoadingResults] = useState({});
  const [individualResults, setIndividualResults] = useState({});

  const fetchParticipationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/assessments/${assessmentId}/participation`);
      setParticipationData(response.data);
    } catch (err) {
      console.error('Failed to fetch participation data:', err);
      setError(err.response?.data?.error || 'Failed to load participation data');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (isOpen && assessmentId) {
      fetchParticipationData();
    }
  }, [isOpen, assessmentId, fetchParticipationData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <FaCheckCircle className="status-icon completed" />;
      case 'Not Started':
      case 'Sent':
        return <FaClock className="status-icon pending" />;
      case 'Accessed':
        return <FaUserCheck className="status-icon accessed" />;
      case 'Expired':
        return <FaExclamationTriangle className="status-icon expired" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getCompletionRate = () => {
    if (!participationData?.summary) return 0;
    return participationData.summary.completion_rate;
  };

  const getCompletionColor = (rate) => {
    if (rate >= 80) return '#10b981'; // Green
    if (rate >= 60) return '#f59e0b'; // Yellow
    if (rate >= 40) return '#ef4444'; // Red
    return '#6b7280'; // Gray
  };

  const fetchIndividualResults = useCallback(async (participantId, participantType) => {
    const resultKey = `${participantType}_${participantId}`;
    if (individualResults[resultKey] || loadingResults[resultKey]) return;

    setLoadingResults(prev => ({ ...prev, [resultKey]: true }));
    
    try {
      let results = null;
      
      if (participantType === 'internal') {
        // For internal participants, check if results are already in participationData
        const participant = participationData?.internal_participants?.find(p => p.id === participantId);
        if (participant?.results) {
          results = participant.results;
        } else {
          // Fallback: fetch from API
          const response = await apiClient.get(`/assessments/${assessmentId}/results`);
          results = response.data;
        }
      } else {
        // For external participants, extract from participationData
        const participant = participationData?.external_participants?.find(p => p.id === participantId);
        results = participant?.results || null;
      }
      
      setIndividualResults(prev => ({
        ...prev,
        [resultKey]: results
      }));
    } catch (err) {
      console.error('Failed to fetch individual results:', err);
      setIndividualResults(prev => ({
        ...prev,
        [resultKey]: { error: 'Failed to load results' }
      }));
    } finally {
      setLoadingResults(prev => ({ ...prev, [resultKey]: false }));
    }
  }, [assessmentId, participationData, individualResults, loadingResults]);

  const toggleResultsExpansion = (participantId, participantType) => {
    const resultKey = `${participantType}_${participantId}`;
    const isExpanding = !expandedResults[resultKey];
    
    setExpandedResults(prev => ({
      ...prev,
      [resultKey]: isExpanding
    }));
    
    if (isExpanding && !individualResults[resultKey]) {
      fetchIndividualResults(participantId, participantType);
    }
  };

  const renderResults = (results) => {
    if (!results) return <div className="no-results">No results available</div>;
    if (results.error) return <div className="error-results">{results.error}</div>;
    
    return (
      <div className="individual-results">
        {Object.entries(results).map(([question, answer], index) => (
          <div key={index} className="result-item">
            <div className="result-question">{question}</div>
            <div className="result-answer">{answer}</div>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content participation-modal">
        <div className="modal-header">
          <h3>
            <FaUsers className="modal-icon" />
            Assessment Participation Tracking
          </h3>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading participation data...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <FaExclamationTriangle />
              <p>{error}</p>
            </div>
          )}

          {participationData && (
            <>
              {/* Assessment Info */}
              <div className="assessment-info">
                <h4>{participationData.assessment.type}</h4>
                <p className="project-name">Project: {participationData.assessment.project_name}</p>
                <p className="assessment-status">Status: {participationData.assessment.status}</p>
              </div>

              {/* Summary Statistics */}
              <div className="participation-summary">
                <div className="summary-grid">
                  <div className="stat-card">
                    <div className="stat-number">{participationData.summary.total_participants}</div>
                    <div className="stat-label">Total Participants</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{participationData.summary.total_completed}</div>
                    <div className="stat-label">Completed</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{participationData.summary.pending}</div>
                    <div className="stat-label">Pending</div>
                  </div>
                  <div className="stat-card">
                    <div 
                      className="stat-number completion-rate"
                      style={{ color: getCompletionColor(getCompletionRate()) }}
                    >
                      {getCompletionRate()}%
                    </div>
                    <div className="stat-label">Completion Rate</div>
                  </div>
                </div>

                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ 
                      width: `${getCompletionRate()}%`,
                      backgroundColor: getCompletionColor(getCompletionRate())
                    }}
                  ></div>
                </div>
              </div>

              {/* Internal Participants */}
              {participationData.internal_participants.length > 0 && (
                <div className="participants-section">
                  <h5>
                    <FaUserCheck className="section-icon" />
                    Internal Stakeholders ({participationData.summary.total_assigned})
                  </h5>
                  <div className="participants-list">
                    {participationData.internal_participants.map(participant => {
                      const resultKey = `internal_${participant.id}`;
                      const isExpanded = expandedResults[resultKey];
                      const isCompleted = participant.status === 'Completed';
                      
                      return (
                        <div key={participant.id} className="participant-item">
                          <div className="participant-main">
                            <div className="participant-info">
                              <div className="participant-name">
                                {getStatusIcon(participant.status)}
                                <span className="name">{participant.name}</span>
                                <span className="email">{participant.email}</span>
                              </div>
                              <div className="participant-details">
                                <span className="job-title">{participant.job_position}</span>
                                {participant.department && (
                                  <span className="department">{participant.department}</span>
                                )}
                              </div>
                            </div>
                            <div className="participant-status">
                              <div className="status-actions">
                                <span className={`status-badge ${participant.status.toLowerCase().replace(' ', '-')}`}>
                                  {participant.status}
                                </span>
                                {isCompleted && (
                                  <button
                                    className="view-results-btn"
                                    onClick={() => toggleResultsExpansion(participant.id, 'internal')}
                                    title="View Results"
                                  >
                                    <FaEye />
                                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                  </button>
                                )}
                              </div>
                              {participant.completion_date && (
                                <div className="completion-date">
                                  Completed: {formatDate(participant.completion_date)}
                                </div>
                              )}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="results-section">
                              <h6>Assessment Results</h6>
                              {loadingResults[resultKey] ? (
                                <div className="loading-results">Loading results...</div>
                              ) : (
                                renderResults(individualResults[resultKey])
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* External Participants */}
              {participationData.external_participants.length > 0 && (
                <div className="participants-section">
                  <h5>
                    <FaEnvelope className="section-icon" />
                    External Stakeholders ({participationData.summary.total_invited})
                  </h5>
                  <div className="participants-list">
                    {participationData.external_participants.map(participant => {
                      const resultKey = `external_${participant.id}`;
                      const isExpanded = expandedResults[resultKey];
                      const isCompleted = participant.status === 'Completed';
                      
                      return (
                        <div key={participant.id} className="participant-item">
                          <div className="participant-main">
                            <div className="participant-info">
                              <div className="participant-name">
                                {getStatusIcon(participant.status)}
                                <span className="name">{participant.name}</span>
                                <span className="email">{participant.email}</span>
                              </div>
                              <div className="participant-details">
                                <span className="invitation-date">
                                  Invited: {formatDate(participant.invited_at)}
                                </span>
                                {participant.expires_at && (
                                  <span className="expiry-date">
                                    Expires: {formatDate(participant.expires_at)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="participant-status">
                              <div className="status-actions">
                                <span className={`status-badge ${participant.status.toLowerCase().replace(' ', '-')}`}>
                                  {participant.status}
                                </span>
                                {isCompleted && (
                                  <button
                                    className="view-results-btn"
                                    onClick={() => toggleResultsExpansion(participant.id, 'external')}
                                    title="View Results"
                                  >
                                    <FaEye />
                                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                  </button>
                                )}
                              </div>
                              {participant.completion_date && (
                                <div className="completion-date">
                                  Completed: {formatDate(participant.completion_date)}
                                </div>
                              )}
                              {participant.accessed_at && !participant.completion_date && (
                                <div className="accessed-date">
                                  Accessed: {formatDate(participant.accessed_at)}
                                </div>
                              )}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="results-section">
                              <h6>Assessment Results</h6>
                              {loadingResults[resultKey] ? (
                                <div className="loading-results">Loading results...</div>
                              ) : (
                                renderResults(individualResults[resultKey])
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {participationData.summary.total_participants === 0 && (
                <div className="empty-state">
                  <FaUsers className="empty-icon" />
                  <h4>No Participants Yet</h4>
                  <p>No stakeholders have been assigned to this assessment yet. Use the "Assign Stakeholders" button to add participants.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="button btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParticipationTrackingModal; 